// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ApiParameter, get, post } from "@insite/client-framework/Services/ApiService";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

const jettingAppApi = "/api/sainc/jetting-app";

export interface Calculator {
    calculatorId: string;
    title: string;
    children: Calculator[];
}

export interface CalculatorMenusResult {
    successful: boolean;
    error?: any;
    calculators: Calculator[];
    statusCode?: number;
}

export interface HoseArray {
    hoseID: string;
    hoseLength: number | undefined;
}

export interface JetArray {
    angle: number;
    bomSeq: number;
    flowPercent: number;
    jetPart: string; // size dropdown value
    jetPath: number;
    label: string; // jets column
    qty: number;
    pdpConfigurationId: number;
}

export interface ChartData {
    xAxisLabel: string;
    title: string;
    seriesList: ChartLineInfo[];
}

export interface ChartLineInfo {
    axis: string;
    axisLabel: string;
    label: string;
    dataList: { x: number; y: number }[];
}
export interface Control {
    additionalInfo: { [key: string]: string };
    controlId: string;
    label?: string;
    pdpConfigurationId: number;
    readOnly: boolean;
    toolTip: string;
    type: string;
    units?: string;
    value: number | string | boolean; // in case of drop down it could be string
    width?: number;
    changedProperties?: string[];
    message: { message: string; type: string };
    options?: { label: string; value: string }[];
    maxValue: number;
    minValue: number;
    step: number;
    hoseArray: HoseArray[];
    hoseOptions: { hoseID: string; hoseDescription: string; sortOrder: number }[];
    jetArray: JetArray[];
    jetOptions: string[];
    chartData: ChartData[];
    resetSuggestJets: boolean;
}

export interface Section {
    controls: Control[];
    displayFlowConverter: boolean | null; // this is for slider units toggle
    convertFlowUnits: boolean | null;
    sectionId: string;
    title: string;
    changedProperties?: string[];
    toolTip?: string;
}

export interface Panel {
    //   alwaysSuggest: boolean;
    panelID: string;
    resetAll: boolean;
    //   resetJetting: boolean;
    sections: Section[];
    title: string;
    toolTip?: string;
    changedProperties?: string[];
}

export interface CalculatorMenuPanelsResult {
    successful: boolean;
    error?: any;
    panels: Panel[];
    onlyCalculateOnButton: boolean;
    initialize: boolean;
    suggestJets: boolean;
    units: string | null;
}

export interface ShareConfigurationModel {
    id: string;
    emailTo: string;
    jettingCalculatorConfig: {};
}

export interface CopyLinkModel {
    uri: string;
    id: string;
    properties: { [key: string]: string };
}

export async function getCalculatorMenus() {
    try {
        const result = await get<CalculatorMenusResult>(`${jettingAppApi}/calculator-menus`);
        return {
            successful: true,
            calculators: result.calculators,
        };
    } catch (error) {
        return {
            successful: false,
            calculators: [],
        };
    }
}

export async function getCalculatorMenuPanels(calculatorId: string, parameter: any) {
    try {
        const result = await post<CalculatorMenuPanelsResult>(`${jettingAppApi}/${calculatorId}/calculate`, parameter);
        return {
            successful: true,
            panels: result.panels,
            onlyCalculateOnButton: result.onlyCalculateOnButton,
            initialize: result.initialize,
            suggestJets: result.suggestJets,
            units: result.units,
        };
    } catch (error) {
        return {
            successful: false,
            panels: [],
            onlyCalculateOnButton: false,
            initialize: true,
            suggestJets: false,
            units: null,
        };
    }
}

export async function loadSavedCalculator(savedConfigId: string) {
    try {
        const result = await get<CalculatorMenuPanelsResult>(
            `${jettingAppApi}/load-calculator-with-config/${savedConfigId}`,
        );
        return {
            successful: true,
            panels: result.panels,
            onlyCalculateOnButton: result.onlyCalculateOnButton,
            initialize: result.initialize,
            suggestJets: result.suggestJets,
            units: result.units,
        };
    } catch (error) {
        return {
            successful: false,
            panels: [],
            onlyCalculateOnButton: false,
            initialize: true,
            suggestJets: false,
            units: null,
        };
    }
}

export async function shareConfiguration(parameter: ShareConfigurationModel) {
    try {
        await post(`${jettingAppApi}/${parameter.id}/email-calculator`, {
            emailTo: parameter.emailTo,
            jettingCalculatorConfig: parameter.jettingCalculatorConfig,
        });

        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
        };
    }
}

export async function copyConfiguration(calculatorId: string, parameter: any) {
    try {
        const result = await post<CopyLinkModel>(`${jettingAppApi}/${calculatorId}/save-calculator-config`, parameter);
        return {
            successful: true,
            id: result.id,
        };
    } catch (error) {
        return {
            successful: false,
            id: "",
        };
    }
}

const productUrl = "/api/v2/products";
export interface GetProductByErpParameter extends ApiParameter {
    productNumbers: string[];
}

export interface GetProductByErpResult {
    successful: boolean;
    error?: any;
    products: ProductModel[];
}

export interface GetProductByErpModel {
    products: ProductModel[];
}

export async function getProductByErp(parameters: GetProductByErpParameter) {
    try {
        const productData = await get<GetProductByErpModel>(`${productUrl}`, parameters);
        return {
            successful: true,
            products: productData?.products,
        };
    } catch (error) {
        return {
            successful: false,
            error,
            products: [],
        };
    }
}
